import React, { useEffect, useMemo, useState } from "react";
import NewTooltip from "../../../components/Atomos/Tooltip/NewTooltip";
import {
  getEmailLogStats,
  type EmailLogStatsMonthBucket,
  type EmailLogStatsResponse,
} from "../../../Services/EmailLog";

const METRIC_KEYS = [
  "requests",
  "delivered",
  "opened",
  "clicked",
  "bounces",
  "spam",
] as const;

type MetricKey = (typeof METRIC_KEYS)[number];

const METRIC_META: Record<
  MetricKey,
  {
    title: string;
    subtitle: string;
    tooltip: string;
    color: string;
    pctKey?: keyof EmailLogStatsResponse["totals"];
  }
> = {
  requests: {
    title: "Requests",
    subtitle: "Total Outbound",
    tooltip:
      "Total outbound emails sent from the system. This is the baseline for all percentage metrics.",
    color: "#2563eb", // blue
  },
  delivered: {
    title: "Delivered",
    subtitle: "Successful",
    tooltip:
      "Emails successfully delivered to the recipient's mail server. Percentage is calculated against total requests.",
    color: "#10c34a", // green
    pctKey: "deliveredPct",
  },
  opened: {
    title: "Opened",
    subtitle: "Total opens",
    tooltip:
      "Emails opened at least once, based on tracking pixels. Percentage is calculated against total requests.",
    color: "#0891b2", // cyan (was teal-green; too close to Delivered)
    pctKey: "openedPct",
  },
  clicked: {
    title: "Clicked",
    subtitle: "Unique clicks",
    tooltip:
      "Emails with at least one link click recorded. Percentage is calculated against total requests.",
    color: "#7c3aed", // violet
    pctKey: "clickedPct",
  },
  bounces: {
    title: "Bounces",
    subtitle: "Delivery failure",
    tooltip:
      "Emails that failed delivery (bounced). Percentage is calculated against total requests.",
    color: "#ca8a04", // amber/gold (was orange; too close to Spam red)
    pctKey: "bouncesPct",
  },
  spam: {
    title: "Spam Reports",
    subtitle: "Filtered spam (ACS)",
    tooltip:
      "Emails filtered as spam by the recipient's mail system (ACS). Percentage is calculated against total requests.",
    color: "#db2777", // magenta (distinct from Bounces amber)
    pctKey: "spamPct",
  },
};

function formatCount (n: number): string {
  return new Intl.NumberFormat("en-US").format(n || 0);
}

function MetricCard ({
  title,
  subtitle,
  tooltip,
  value,
  pct,
  color,
}: {
  title: string;
  subtitle: string;
  tooltip: string;
  value: number;
  pct?: number;
  color: string;
}): React.ReactElement {
  const isRequests = pct == null;
  const hero = isRequests ? formatCount(value) : `${pct}%`;

  return (
    <div className="bg-white min-h-[78px] px-3 py-2 md:px-4 rounded border border-gray-200 shadow-sm flex items-center gap-3">
      <span
        className="h-3 w-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex items-center gap-1.5 text-xs md:text-sm font-medium text-gray-font leading-tight font-decimal-medium">
          <span>{title}</span>
          <NewTooltip text={tooltip} />
        </div>
        <div className="text-2xl md:text-[28px] font-semibold text-gray-font leading-none">
          {hero}
        </div>
        <div className="text-xs text-gray-500">
          <span className="font-medium text-gray-font">{formatCount(value)}</span>
          {" "}
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function MonthlyLineChart ({
  byMonth,
}: {
  byMonth: EmailLogStatsMonthBucket[];
}): React.ReactElement {
  const maxValue = useMemo(() => {
    let max = 0;
    for (const bucket of byMonth) {
      for (const key of METRIC_KEYS) {
        max = Math.max(max, Number(bucket[key] || 0));
      }
    }
    return Math.max(max, 1);
  }, [byMonth]);

  const width = 720;
  const height = 240;
  const padding = { top: 20, right: 16, bottom: 40, left: 44 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const pointCount = Math.max(byMonth.length, 1);

  const xAt = (index: number) =>
    padding.left +
    (pointCount === 1 ? chartW / 2 : (index / (pointCount - 1)) * chartW);

  const yAt = (value: number) =>
    padding.top + chartH - (value / maxValue) * chartH;

  const seriesPoints = METRIC_KEYS.map((key) => ({
    key,
    points: byMonth.map((bucket, index) => {
      const value = Number(bucket[key] || 0);
      return {
        x: xAt(index),
        y: yAt(value),
        value,
        label: bucket.label,
      };
    }),
  }));

  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full min-w-[560px] h-auto"
        role="img"
        aria-label="Email status counts by month for the last 6 months"
      >
        {yTicks.map((t) => {
          const y = padding.top + chartH - t * chartH;
          const label = Math.round(maxValue * t);
          return (
            <g key={t}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + chartW}
                y2={y}
                stroke="#e5e7eb"
                strokeDasharray={t === 0 ? undefined : "4 4"}
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                textAnchor="end"
                className="fill-gray-400"
                fontSize="10"
              >
                {label}
              </text>
            </g>
          );
        })}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={padding.top + chartH}
          stroke="#d1d5db"
        />
        <line
          x1={padding.left}
          y1={padding.top + chartH}
          x2={padding.left + chartW}
          y2={padding.top + chartH}
          stroke="#d1d5db"
        />

        {seriesPoints.map(({ key, points }) => {
          if (points.length === 0) {
            return null;
          }
          const path = points
            .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
            .join(" ");
          return (
            <g key={key}>
              <path
                d={path}
                fill="none"
                stroke={METRIC_META[key].color}
                strokeWidth={1}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {points.map((p) => (
                <circle
                  key={`${key}-${p.label}`}
                  cx={p.x}
                  cy={p.y}
                  r={2}
                  fill="#fff"
                  stroke={METRIC_META[key].color}
                  strokeWidth={1}
                >
                  <title>
                    {p.label} · {METRIC_META[key].title}: {p.value}
                  </title>
                </circle>
              ))}
            </g>
          );
        })}

        {byMonth.map((bucket, index) => (
          <text
            key={`${bucket.year}-${bucket.month}`}
            x={xAt(index)}
            y={padding.top + chartH + 20}
            textAnchor="middle"
            className="fill-gray-600"
            fontSize="11"
          >
            {bucket.label}
          </text>
        ))}
      </svg>
      <div className="mt-3 flex flex-wrap gap-4">
        {METRIC_KEYS.map((key) => (
          <div key={key} className="flex items-center gap-2 text-xs text-gray-700">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full border-2 border-solid bg-white"
              style={{ borderColor: METRIC_META[key].color }}
            />
            {METRIC_META[key].title}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function EmailStatsDashboard (): React.ReactElement | null {
  const [data, setData] = useState<EmailLogStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getEmailLogStats()
      .then((stats) => {
        if (cancelled) {
          return;
        }
        if (!stats) {
          setError("Unable to load email stats.");
          setData(null);
          return;
        }
        setData(stats);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Unable to load email stats.");
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return null;
  }

  if (error || !data) {
    return (
      <div className="rounded border border-red-100 bg-red-50 p-4 text-sm text-red-700">
        {error || "Unable to load email stats."}
      </div>
    );
  }

  const { totals, byMonth } = data;

  return (
    <div className=" flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
        {METRIC_KEYS.map((key) => {
          const meta = METRIC_META[key];
          const pct =
            meta.pctKey != null
              ? Number(totals[meta.pctKey] ?? 0)
              : undefined;
          return (
            <MetricCard
              key={key}
              title={meta.title}
              subtitle={meta.subtitle}
              tooltip={meta.tooltip}
              value={Number(totals[key] || 0)}
              pct={pct}
              color={meta.color}
            />
          );
        })}
      </div>
      <div className="text-sm text-gray-500">
        <p>
          The chart is only available for the last 6 months.
        </p>
      </div>
      <div className="rounded border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-font mb-2">
          Status by month
        </h3>
        <MonthlyLineChart byMonth={byMonth ?? []} />
      </div>
    </div>
  );
}
